if (!customElements.get('pp-header')) {
  class PpHeader extends HTMLElement {
    constructor() {
      super();
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.refreshCartCount = this.refreshCartCount.bind(this);
      this.onDrawerKeydown = this.onDrawerKeydown.bind(this);
    }

    connectedCallback() {
      this.search = this.querySelector('[data-search]');
      this.cartCount = this.querySelector('[data-cart-count]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.drawer = this.querySelector('[data-drawer]');
      this.drawerOpener = this.querySelector('[data-drawer-open]');

      if (this.drawer && this.drawerOpener) {
        this.drawerOpener.addEventListener('click', () => this.openDrawer());
        this.drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeDrawer());
        });
        this.drawer.querySelectorAll('[data-drawer-tab]').forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab));
        });
      }

      if (this.dataset.sticky === 'true') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      if (this.search) {
        const closeBtn = this.search.querySelector('[data-search-close]');
        if (closeBtn) closeBtn.addEventListener('click', () => this.closeSearch(true));

        this.search.addEventListener('toggle', () => {
          if (this.search.open) {
            const input = this.search.querySelector('input[type="search"]');
            if (input) window.setTimeout(() => input.focus(), 50);
          }
        });

        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
      }

      // Keep the cart count in sync with Dawn's cart events (Dawn 10+ ships pubsub.js).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refreshCartCount);
      }
      // Horizon-based themes dispatch a cart:update event on the document instead.
      document.addEventListener('cart:update', this.refreshCartCount);
      document.addEventListener('pp:cart-updated', this.refreshCartCount);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refreshCartCount();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('cart:update', this.refreshCartCount);
      document.removeEventListener('pp:cart-updated', this.refreshCartCount);
      document.removeEventListener('keydown', this.onDrawerKeydown);
      document.body.classList.remove('pp-drawer-open');
      if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
    }

    openDrawer() {
      this.drawer.hidden = false;
      document.body.classList.add('pp-drawer-open');
      this.drawerOpener.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onDrawerKeydown);
      window.requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        const panel = this.drawer.querySelector('.pp-drawer__panel');
        if (panel) panel.focus();
      });
    }

    selectTab(selected) {
      this.drawer.querySelectorAll('[data-drawer-tab]').forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('pp-drawer-open');
      this.drawerOpener.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onDrawerKeydown);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => {
        this.drawer.hidden = true;
        this.drawerOpener.focus();
      }, reduced ? 0 : 300);
    }

    onDrawerKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.drawer.querySelectorAll('a[href], button, summary, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.classList.toggle('is-scrolled', window.scrollY > 4);
      });
    }

    closeSearch(returnFocus) {
      if (!this.search || !this.search.open) return;
      this.search.open = false;
      if (returnFocus) {
        const summary = this.search.querySelector('summary');
        if (summary) summary.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeSearch(true);
    }

    onDocumentClick(event) {
      if (this.search && this.search.open && !this.search.contains(event.target)) {
        this.closeSearch(false);
      }
    }

    refreshCartCount() {
      if (!this.cartCount) return;
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

      fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const count = cart.item_count || 0;
          this.cartCount.textContent = count > 99 ? '99+' : String(count);
          this.cartCount.classList.toggle('is-empty', count === 0);
          if (this.cartLink) this.cartLink.setAttribute('aria-label', 'Cart, ' + count + ' items');
        })
        .catch(() => {});
    }
  }

  customElements.define('pp-header', PpHeader);
}
